import { useCallback, useEffect, useMemo, useRef, useState } from "react";
// Type-only imports — erased at build time. livekit-client runtime values are
// lazy-loaded inside joinVoice so the bundle only pays for them on first join.
import type { Room as LKRoom, RemoteParticipant, RemoteTrack, RemoteTrackPublication } from "livekit-client";
import { api } from "../../../lib/api";
import { toast } from "../../../lib/ui";
import { type AudioRoute, getAvailableRoutes, isAndroidNative } from "../../../lib/audioRoute";

// livekit-client surfaces internal SDK wording that reads as nonsense to a user —
// most notably "Client initiated disconnect", which it throws when the pending
// connect() is torn down mid-negotiation. That happens on a retry or a leave while
// ICE is still failing, so the user sees a message blaming them for a hang-up they
// never made, and the real cause (no media path to the server) stays hidden.
function voiceJoinErrorMessage(err: any): string {
  const raw = String(err?.message || '').trim();
  if (/client initiated disconnect|cancelled/i.test(raw)) return 'Voice connection cancelled';
  if (/could not establish pc connection|ice|timed? ?out/i.test(raw)) {
    return "Couldn't reach the voice server — check your connection and try again";
  }
  if (/permission|not allowed|denied/i.test(raw)) return 'Microphone permission is required to join voice';
  if (/token/i.test(raw)) return 'Voice access was refused — try rejoining';
  return raw || 'Failed to join voice';
}

// Structural copy of ChatLayout's VoiceParticipant — kept identical on purpose
// (rule 5: extracted files define their own types, no imports back into ChatLayout).
type VoiceParticipant = { id: string; name: string; stream: MediaStream | null; avatar?: string | null; isSelf?: boolean; speaking?: boolean; muted?: boolean };

// Structural subsets of ChatLayout's member/channel shapes — only what voice reads.
type VoiceMember = { id: string; name?: string; username?: string; avatarUrl?: string | null };
type VoiceChannelMeta = { id: string; name?: string };

type UseVoiceCallOptions = {
  socket: any;
  token: string;
  me: { userId?: string; name?: string; avatarUrl?: string | null };
  user: { username?: string | null } | null;
  members: VoiceMember[];
  channels: VoiceChannelMeta[];
  currentVoidId: string;
  currentChannelId: string;
  livekitUrl: string;
  /**
   * confirmVoiceSwitch needs to move the UI to the target space/channel, but
   * space/channel switching (switchVoid/switchChannel/pendingChannelSwitchRef)
   * stays parent-side — the parent supplies this small nav callback instead.
   */
  navigateToVoiceChannel: (spaceId: string, channelId: string) => void;
};

// Module-level copy of ChatLayout's pure fq helper (same pattern as
// useAndroidShare's normalizeChannelId copy): fully-qualify a channel id.
const fq = (vId: string, cId: string) => (cId.includes(':') ? cId : `${vId}:${cId}`);

async function enableMicWithRetry(room: LKRoom, opts: any, attempts = 3) {
  let lastErr: any = null;
  for (let i = 0; i < attempts; i += 1) {
    try {
      await room.localParticipant.setMicrophoneEnabled(true, opts);
      return;
    } catch (e: any) {
      lastErr = e;
      const msg = String(e?.message || "").toLowerCase();
      const retryable = msg.includes("engine not connected") || msg.includes("within timeout");
      if (!retryable || i === attempts - 1) break;
      await new Promise((r) => setTimeout(r, 200 * (i + 1)));
    }
  }
  throw lastErr || new Error("Failed to enable microphone");
}

/**
 * Voice/video channel calls over LiveKit: join/leave/switch lifecycle, mute and
 * deafen, per-participant volume, level meters, device selection, remote
 * audio/video attachment, join/leave chimes, and the voice:presence roster.
 * Extracted verbatim from ChatLayout (step 2.2b) — the dock/JSX consumers stay
 * parent-side until step 2.3.
 */
export function useVoiceCall({
  socket,
  token,
  me,
  user,
  members,
  channels,
  currentVoidId,
  currentChannelId,
  livekitUrl,
  navigateToVoiceChannel,
}: UseVoiceCallOptions) {
  const [voiceJoined, setVoiceJoined] = useState(false);
  const [voiceMuted, setVoiceMuted] = useState(false);
  const [voicePeers, setVoicePeers] = useState<Record<string, { userId?: string; name?: string; hasVideo?: boolean; avatarUrl?: string | null }>>({});
  const [voiceActiveChannelId, setVoiceActiveChannelId] = useState<string | null>(null);
  const [voiceActiveChannelName, setVoiceActiveChannelName] = useState<string | null>(null);
  const [voiceActiveSpaceId, setVoiceActiveSpaceId] = useState<string | null>(null);
  const [voiceSwitchPrompt, setVoiceSwitchPrompt] = useState<{ channelId: string; spaceId: string } | null>(null);
  const [voicePresenceByChannel, setVoicePresenceByChannel] = useState<Record<string, string[]>>({});
  const [videoEnabled, setVideoEnabled] = useState(false);
  const [videoBusy, setVideoBusy] = useState(false);
  const [localVideoStream, setLocalVideoStream] = useState<MediaStream | null>(null);
  const [remoteVideoStreams, setRemoteVideoStreams] = useState<Record<string, MediaStream>>({});
  const remoteStreamsRef = useRef(new Map<string, MediaStream>());
  const liveKitRoomRef = useRef<LKRoom | null>(null);
  // Room object for a join that is still negotiating. Kept separate from
  // liveKitRoomRef so an in-flight join is never mistaken for an established call
  // (see joinVoice), while still being reachable so leaveVoice can abort it.
  const pendingJoinRef = useRef<LKRoom | null>(null);
  const isAndroidClient = useMemo(() => /android/i.test(typeof navigator !== "undefined" ? navigator.userAgent || "" : ""), []);
  const localStreamRef = useRef<MediaStream | null>(null);
  const [voiceLevels, setVoiceLevels] = useState<Record<string, number>>({});
  const audioCtxRef = useRef<AudioContext | null>(null);
  const meterNodesRef = useRef(new Map<string, { analyser: AnalyserNode; data: Uint8Array<ArrayBuffer>; source: MediaStreamAudioSourceNode }>());
  const meterLoopRef = useRef<number | null>(null);
  const meterLastUpdateAtRef = useRef(0);
  const debugVoiceRef = useRef(false);
  const voiceChimeCtxRef = useRef<AudioContext | null>(null);
  // Device management for voice
  const [devicesOpen, setDevicesOpen] = useState(false);
  const [inputs, setInputs] = useState<MediaDeviceInfo[]>([]);
  const [outputs, setOutputs] = useState<MediaDeviceInfo[]>([]);
  const [selectedInputId, setSelectedInputId] = useState<string | null>(null);
  const [selectedOutputId, setSelectedOutputId] = useState<string | null>(null);
  const toggleDevices = useCallback(() => setDevicesOpen((v) => !v), []);
  const audioElsRef = useRef<Map<string, HTMLAudioElement>>(new Map());
  const [needsAudioUnlock, setNeedsAudioUnlock] = useState(false);
  const [voiceVolumes, setVoiceVolumes] = useState<Record<string, number>>({});
  const [voiceSilenced] = useState<Record<string, boolean>>({});
  const [voiceAudioRoute, setVoiceAudioRoute] = useState<AudioRoute>('earpiece');
  const [voiceAvailableRoutes, setVoiceAvailableRoutes] = useState<AudioRoute[]>([]);
  const [voiceDeafened, setVoiceDeafened] = useState(false);
  const voiceDeafenedRef = useRef(false);
  const voiceJoinedRef = useRef(false);
  useEffect(() => { voiceJoinedRef.current = voiceJoined; }, [voiceJoined]);
  const memberNameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const m of members) {
      const label = (m?.name || m?.username || '').trim();
      if (label) map.set(m.id, label);
    }
    return map;
  }, [members]);
  const getVoiceNamesForChannel = useCallback((channelId: string) => {
    // Presence is keyed by fq (spaceId:channelId); fq() is a no-op for ids that already embed the space
    const key = currentVoidId ? fq(currentVoidId, channelId) : channelId;
    const ids = voicePresenceByChannel[key] || voicePresenceByChannel[channelId] || [];
    return ids.map((uid) => memberNameById.get(uid) || uid.slice(0, 8));
  }, [voicePresenceByChannel, memberNameById, currentVoidId]);

  useEffect(() => {
    try {
      debugVoiceRef.current = localStorage.getItem("debugVoice") === "1";
    } catch {
      debugVoiceRef.current = false;
    }
  }, []);

  // NOTE: we intentionally do NOT wipe voicePresenceByChannel on space change.
  // The voice:presence handler already replaces each space's roster wholesale on
  // every event, and the server re-sends a fresh snapshot for the space we switch
  // INTO (void:switch/space:switch -> sendVoicePresenceToSocket). A blanket wipe
  // here was redundant and — worse — raced that re-send: on fast/local connections
  // the snapshot could arrive first and then get clobbered by the wipe, leaving the
  // roster permanently empty until the next join/leave. Stale entries for spaces
  // we've navigated away from are harmless (those channels aren't rendered) and are
  // refreshed the moment we return to that space.

  const logVoice = (...args: unknown[]) => {
    if (debugVoiceRef.current) {
      console.debug("[voice]", ...args);
    }
  };

  const playVoiceChime = (kind: "join" | "leave") => {
    try {
      const AudioCtor =
        window.AudioContext ||
        (window as typeof window & { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
      if (!AudioCtor) return;
      if (!voiceChimeCtxRef.current || voiceChimeCtxRef.current.state === "closed") {
        voiceChimeCtxRef.current = new AudioCtor();
      }
      const ctx = voiceChimeCtxRef.current;
      if (!ctx) return;
      if (ctx.state === "suspended") {
        ctx.resume().catch(() => {});
      }
      const now = ctx.currentTime;
      const gain = ctx.createGain();
      gain.gain.setValueAtTime(0.0001, now);
      gain.gain.linearRampToValueAtTime(0.18, now + 0.01);
      gain.gain.exponentialRampToValueAtTime(0.0001, now + (kind === "join" ? 0.2 : 0.24));
      gain.connect(ctx.destination);

      const playTone = (frequency: number, start: number, duration: number) => {
        const osc = ctx.createOscillator();
        osc.type = "sine";
        osc.frequency.setValueAtTime(frequency, start);
        osc.connect(gain);
        osc.start(start);
        osc.stop(start + duration);
      };

      if (kind === "join") {
        playTone(660, now, 0.08);
        playTone(880, now + 0.08, 0.08);
      } else {
        playTone(520, now, 0.1);
        playTone(390, now + 0.1, 0.1);
      }
    } catch {
      // ignore autoplay restrictions or missing audio
    }
  };
  const voiceParticipants = useMemo(() => {
    const arr: VoiceParticipant[] = [];
    const selfName = me?.name || user?.username || "You";
    arr.push({
      id: "self",
      name: selfName,
      stream: videoEnabled ? localVideoStream : null,
      avatar: me?.avatarUrl || null,
      isSelf: true,
      speaking: (voiceLevels["self"] || 0) > 0.2,
      muted: voiceMuted,
    });
    for (const [peerId, info] of Object.entries(voicePeers)) {
      const member = members.find((mm) => mm.id === info?.userId);
      arr.push({
        id: peerId,
        name: info?.name || member?.name || member?.username || "Member",
        stream: remoteVideoStreams[peerId] || null,
        avatar: member?.avatarUrl || null,
        speaking: (voiceLevels[peerId] || 0) > 0.2,
        muted: !!voiceSilenced[peerId],
      });
    }
    return arr;
  }, [voicePeers, members, localVideoStream, remoteVideoStreams, videoEnabled, me?.name, me?.avatarUrl, user?.username, voiceLevels, voiceMuted, voiceSilenced]);
  // True only when the channel being VIEWED is the one we're actually connected to.
  // Without this, every voice channel view rendered the connected room's participants.
  const viewingActiveCall = voiceJoined && voiceActiveChannelId === currentChannelId && voiceActiveSpaceId === currentVoidId;
  const voiceRoomParticipants = useMemo(() => {
    if (viewingActiveCall) return voiceParticipants;
    const fqid = currentVoidId ? fq(currentVoidId, currentChannelId) : currentChannelId;
    const ids = voicePresenceByChannel[fqid] || voicePresenceByChannel[currentChannelId] || [];
    return ids.map((id) => {
      const member = members.find((m) => m.id === id);
      return {
        id,
        name: member?.name || member?.username || id.slice(0, 8),
        stream: null,
        avatar: member?.avatarUrl || null,
        muted: false,
      } satisfies VoiceParticipant;
    });
  }, [viewingActiveCall, voiceParticipants, voicePresenceByChannel, currentVoidId, currentChannelId, members]);

  function refreshLiveKitPeers(room: LKRoom) {
    const next: Record<string, { userId?: string; name?: string; hasVideo?: boolean; avatarUrl?: string | null }> = {};
    room.remoteParticipants.forEach((p: RemoteParticipant) => {
      const hasVideo = !!p.getTrackPublication("camera" as any)?.isSubscribed;
      const member = members.find((mm) => mm.id === p.identity);
      next[p.identity] = {
        userId: p.identity,
        name: member?.name || p.name || p.identity,
        avatarUrl: member?.avatarUrl || null,
        hasVideo,
      };
    });
    setVoicePeers(next);
  }

  function clearLiveKitState() {
    remoteStreamsRef.current.clear();
    for (const participantId of Array.from(audioElsRef.current.keys())) {
      removeRemoteAudioElement(participantId);
    }
    setRemoteVideoStreams({});
    setVoicePeers({});
    stopMeter('self');
    meterNodesRef.current.clear();
  }

  function startMetersLoop() {
    if (meterLoopRef.current != null) return;
    const tick = () => {
      if (typeof document !== 'undefined' && document.visibilityState !== 'visible') {
        meterLoopRef.current = requestAnimationFrame(tick);
        return;
      }
      const now = typeof performance !== 'undefined' ? performance.now() : Date.now();
      const minUpdateMs = isAndroidClient ? 160 : 66;
      if ((now - meterLastUpdateAtRef.current) < minUpdateMs) {
        meterLoopRef.current = requestAnimationFrame(tick);
        return;
      }
      meterLastUpdateAtRef.current = now;
      const levels: Record<string, number> = {};
      for (const [id, node] of meterNodesRef.current.entries()) {
        const { analyser, data } = node;
        analyser.getByteTimeDomainData(data);
        // Compute simple peak normalized 0..1
        let peak = 0;
        for (let i = 0; i < data.length; i++) {
          const v = (data[i] - 128) / 128; // -1..1
          const a = Math.abs(v);
          if (a > peak) peak = a;
        }
        levels[id] = Math.min(1, peak * 1.5);
      }
      if (Object.keys(levels).length > 0) setVoiceLevels(prev => ({ ...prev, ...levels }));
      meterLoopRef.current = requestAnimationFrame(tick);
    };
    meterLoopRef.current = requestAnimationFrame(tick);
  }
  function stopMetersLoop() {
    if (meterLoopRef.current != null) { cancelAnimationFrame(meterLoopRef.current); meterLoopRef.current = null; }
  }
  useEffect(() => {
    const onVisibility = () => {
      try {
        if (document.visibilityState === 'visible') {
          if (audioCtxRef.current?.state === 'suspended') audioCtxRef.current.resume().catch(() => {});
          if (meterNodesRef.current.size > 0) startMetersLoop();
        } else {
          stopMetersLoop();
          if (audioCtxRef.current?.state === 'running') audioCtxRef.current.suspend().catch(() => {});
        }
      } catch { /* noop */ }
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  // eslint-disable-next-line react-hooks/exhaustive-deps -- registered once; startMetersLoop only touches refs and state setters
  }, []);
  function startMeter(id: string, stream: MediaStream) {
    try {
      if (!audioCtxRef.current) {
        const AudioCtor = window.AudioContext || (window as typeof window & { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
        audioCtxRef.current = AudioCtor ? new AudioCtor() : null;
      }
      if (!audioCtxRef.current) return;
      const ctx = audioCtxRef.current!;
      if (ctx.state === 'suspended') { ctx.resume().catch(()=>{}); }
      if (meterNodesRef.current.has(id)) return;
      const source = ctx.createMediaStreamSource(stream);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = isAndroidClient ? 256 : 512;
      const data: Uint8Array<ArrayBuffer> = new Uint8Array(new ArrayBuffer(analyser.fftSize));
      source.connect(analyser);
      meterNodesRef.current.set(id, { analyser, data, source });
      startMetersLoop();
    } catch { /* noop */ }
  }
  function stopMeter(id: string) {
    const node = meterNodesRef.current.get(id);
    if (!node) return;
    try { for (const t of node.source.mediaStream.getTracks()) t.stop(); } catch { /* noop */ }
    try { node.source.disconnect(); } catch { /* noop */ }
    meterNodesRef.current.delete(id);
    setVoiceLevels(prev => { const n = { ...prev }; delete n[id]; return n; });
    if (meterNodesRef.current.size === 0) stopMetersLoop();
  }

  async function refreshDevices() {
    try {
      const list = await navigator.mediaDevices.enumerateDevices();
      const ins = list.filter(d => d.kind === 'audioinput');
      const outs = list.filter(d => d.kind === 'audiooutput');
      setInputs(ins);
      setOutputs(outs);
      // Try to align selection with current stream
      const cur = localStreamRef.current?.getAudioTracks?.()[0];
      const curSettings = cur?.getSettings?.();
      const curId = curSettings && typeof curSettings.deviceId === 'string' ? curSettings.deviceId : null;
      if (curId && !selectedInputId) setSelectedInputId(curId);
    } catch { /* noop */ }
  }

  async function applyOutputDevice(sinkId: string | null) {
    try {
      for (const el of audioElsRef.current.values()) {
        try {
          const withSink = el as HTMLMediaElement & { setSinkId?: (sinkId: string) => Promise<void> };
          if (withSink && typeof withSink.setSinkId === 'function') {
            await withSink.setSinkId(sinkId ?? '');
          }
        } catch { /* noop */ }
      }
    } catch { /* noop */ }
  }

  function removeRemoteAudioElement(participantId: string) {
    const el = audioElsRef.current.get(participantId);
    if (!el) return;
    try { el.pause(); } catch { /* noop */ }
    try { el.srcObject = null; } catch { /* noop */ }
    try { el.remove(); } catch { /* noop */ }
    audioElsRef.current.delete(participantId);
  }

  async function attachRemoteAudio(participant: RemoteParticipant, track: RemoteTrack) {
    if (track.kind !== "audio") return;
    removeRemoteAudioElement(participant.identity);
    try {
      const el = track.attach() as HTMLAudioElement;
      el.autoplay = true;
      el.style.display = 'none';
      document.body.appendChild(el);
      audioElsRef.current.set(participant.identity, el);
      if (selectedOutputId && typeof el.setSinkId === 'function') {
        await el.setSinkId(selectedOutputId);
      }
      el.volume = voiceVolumes[participant.identity] ?? 1;
      el.muted = voiceDeafenedRef.current || !!voiceSilenced[participant.identity];
      await el.play();
      setNeedsAudioUnlock(false);
    } catch {
      setNeedsAudioUnlock(true);
    }
  }

  async function unlockRemoteAudio() {
    try { await liveKitRoomRef.current?.startAudio(); } catch { /* noop */ }
    try { await audioCtxRef.current?.resume(); } catch { /* noop */ }
    for (const el of audioElsRef.current.values()) {
      try { await el.play(); } catch {
        setNeedsAudioUnlock(true);
        return;
      }
    }
    setNeedsAudioUnlock(false);
  }

  function toggleDeafen() {
    setVoiceDeafened((muted) => {
      const next = !muted;
      voiceDeafenedRef.current = next;
      for (const [participantId, el] of audioElsRef.current.entries()) {
        try { el.muted = next || !!voiceSilenced[participantId]; } catch { /* noop */ }
      }
      return next;
    });
  }

  function setParticipantVolume(participantId: string, volume: number) {
    const clamped = Math.max(0, Math.min(2, volume));
    setVoiceVolumes((prev) => ({ ...prev, [participantId]: clamped }));
    const el = audioElsRef.current.get(participantId);
    if (el) {
      try { el.volume = clamped; } catch { /* noop */ }
    }
  }

  async function switchMicrophone(deviceId: string | null) {
    if (!liveKitRoomRef.current) {
      toast('Join the call before switching devices', 'info');
      return;
    }
    try {
      const captureOpts: any = {
        echoCancellation: true,
        noiseSuppression: true,
        channelCount: 1,
        // Android WebView can clip/crackle with AGC enabled.
        autoGainControl: !isAndroidClient,
      };
      if (deviceId) captureOpts.deviceId = deviceId;
      await enableMicWithRetry(liveKitRoomRef.current, captureOpts);
    } catch (e: any) {
      toast(e?.message || 'Failed to switch microphone', 'error');
    }
  }

  // Refresh devices when joining voice or opening the devices panel
  useEffect(() => {
    if (voiceJoined && devicesOpen) refreshDevices();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- runs when joining/opening devices; uses the current render's refreshDevices
  }, [voiceJoined, devicesOpen]);

  // Re-apply output device when selection changes
  useEffect(() => {
    if (voiceJoined) applyOutputDevice(selectedOutputId || null);
  }, [selectedOutputId, voiceJoined]);

  // voice:presence roster updates. Registered as the hook's own listener instead
  // of inside ChatLayout's big shared socket effect (socket.io allows multiple
  // listeners per event). The handler is a pure functional state update with no
  // closure over space/channel/token, so unlike the shared effect it doesn't
  // need to re-register when those change.
  useEffect(() => {
    if (!socket) return;
    const onVoicePresence = (payload: { spaceId?: string; channels?: Record<string, string[]> }) => {
      const spaceId = payload?.spaceId;
      if (!spaceId) return;
      const raw = payload.channels || {};
      // Keep server fq keys (spaceId:channelId) as-is. Stripping them to bare channel
      // names made presence collide across spaces (every space has a "general").
      // Merge per-space: replace only this space's entries so other spaces' data
      // stays correct and stale entries for this space are dropped.
      setVoicePresenceByChannel(prev => {
        const next: Record<string, string[]> = {};
        for (const [key, users] of Object.entries(prev)) {
          if (!key.startsWith(`${spaceId}:`)) next[key] = users;
        }
        for (const [key, users] of Object.entries(raw)) next[key] = users;
        return next;
      });
    };
    socket.on("voice:presence", onVoicePresence);
    return () => { socket.off("voice:presence", onVoicePresence); };
  }, [socket]);

  // Re-announce voice presence after a socket reconnect. Voice presence is keyed to
  // the socket on the server: when the old socket drops, its 'disconnect' handler runs
  // voiceLeave() and removes us from the roster — but the LiveKit call (a separate
  // WebRTC connection) survives the blip. Without re-announcing, we'd silently vanish
  // from everyone's channel-list roster while still talking. socket.io fires 'connect'
  // on every (re)connection; we only re-emit when an actual call is live (the ref is
  // the source of truth, cleared synchronously by leaveVoice).
  useEffect(() => {
    if (!socket) return;
    const onReconnect = () => {
      if (!liveKitRoomRef.current) return;
      const sid = voiceActiveSpaceId;
      const cid = voiceActiveChannelId;
      if (!sid || !cid) return;
      try { socket.emit("voice:join", { spaceId: sid, channelId: fq(sid, cid) }); } catch { /* noop */ }
    };
    socket.on("connect", onReconnect);
    return () => { socket.off("connect", onReconnect); };
  }, [socket, voiceActiveSpaceId, voiceActiveChannelId]);

  // --- Voice controls ---
  async function joinVoice(targetSpaceId?: string, targetChannelId?: string) {
    // Guard: React onClick passes a SyntheticEvent as first arg — reject non-strings
    const spaceId = typeof targetSpaceId === "string" ? targetSpaceId : currentVoidId;
    const channelId = typeof targetChannelId === "string" ? targetChannelId : currentChannelId;
    if (!spaceId || !channelId) return;
    // Guard on the ref, not voiceJoined state: confirmVoiceSwitch calls joinVoice from a
    // render where voiceJoined was still true (stale closure). The ref is cleared
    // synchronously by leaveVoice, so it reflects the real connection.
    // A join that is still negotiating is not a call we can switch away from: the
    // active channel state is not set until connect() resolves, so every channel
    // would look "different" and prompt to switch out of a call that never started.
    if (pendingJoinRef.current) {
      toast('Still connecting to voice…', 'info');
      return;
    }
    if (liveKitRoomRef.current) {
      const differentChannel = channelId !== voiceActiveChannelId || spaceId !== voiceActiveSpaceId;
      if (differentChannel) {
        setVoiceSwitchPrompt({ channelId, spaceId });
      }
      return;
    }
    let room: LKRoom | null = null;
    try {
      if (!livekitUrl) {
        toast('LiveKit is not configured yet', 'error');
        return;
      }
      // Lazy-load LiveKit — only downloaded when the user first joins a voice channel
      const { Room, RoomEvent, Track } = await import("livekit-client");
      const lk = await api.postAuth('/voice/token', { spaceId, channelId: fq(spaceId, channelId) }, token);
      const lkToken = String(lk?.token || '');
      if (!lkToken) throw new Error('LiveKit token missing');
      room = new Room({
        adaptiveStream: true,
        dynacast: true,
      });
      const activeRoom = room;
      pendingJoinRef.current = activeRoom;
      activeRoom.on(RoomEvent.ParticipantConnected, () => refreshLiveKitPeers(activeRoom));
      activeRoom.on(RoomEvent.ParticipantDisconnected, (p: RemoteParticipant) => {
        try { remoteStreamsRef.current.delete(p.identity); } catch {}
        removeRemoteAudioElement(p.identity);
        setRemoteVideoStreams(prev => { const next = { ...prev }; delete next[p.identity]; return next; });
        stopMeter(p.identity);
        refreshLiveKitPeers(activeRoom);
      });
      activeRoom.on(RoomEvent.TrackSubscribed, (track: RemoteTrack, _pub: RemoteTrackPublication, participant: RemoteParticipant) => {
        if (track.kind === Track.Kind.Audio) {
          const ms = new MediaStream([track.mediaStreamTrack.clone()]);
          startMeter(participant.identity, ms);
          void attachRemoteAudio(participant, track);
        } else if (track.kind === Track.Kind.Video) {
          const ms = new MediaStream([track.mediaStreamTrack]);
          setRemoteVideoStreams(prev => ({ ...prev, [participant.identity]: ms }));
        }
        refreshLiveKitPeers(activeRoom);
      });
      activeRoom.on(RoomEvent.TrackUnsubscribed, (track: RemoteTrack, _pub: RemoteTrackPublication, participant: RemoteParticipant) => {
        if (track.kind === Track.Kind.Audio) {
          remoteStreamsRef.current.delete(participant.identity);
          removeRemoteAudioElement(participant.identity);
          stopMeter(participant.identity);
        } else if (track.kind === Track.Kind.Video) {
          setRemoteVideoStreams(prev => { const next = { ...prev }; delete next[participant.identity]; return next; });
        }
        refreshLiveKitPeers(activeRoom);
      });
      activeRoom.on(RoomEvent.Disconnected, () => {
        try {
          socket.emit('voice:leave', { spaceId, channelId: fq(spaceId, channelId) });
        } catch { /* noop */ }
        clearLiveKitState();
        setVoiceJoined(false);
        setVoiceActiveChannelId(null);
        setVoiceActiveChannelName(null);
        setVoiceActiveSpaceId(null);
        setVoiceMuted(false);
        voiceDeafenedRef.current = false;
        setVoiceDeafened(false);
        setVideoEnabled(false);
        setLocalVideoStream(null);
      });
      await activeRoom.connect(livekitUrl, lkToken, { autoSubscribe: true });
      // Connected: promote to the active room so leaveVoice/mute/video act on it.
      liveKitRoomRef.current = activeRoom;
      pendingJoinRef.current = null;
      const micCaptureOpts: any = {
        echoCancellation: true,
        noiseSuppression: true,
        channelCount: 1,
        autoGainControl: !isAndroidClient,
      };
      if (selectedInputId) micCaptureOpts.deviceId = selectedInputId;
      await enableMicWithRetry(activeRoom, micCaptureOpts);
      try { await activeRoom.startAudio(); setNeedsAudioUnlock(false); } catch { setNeedsAudioUnlock(true); }
      if (isAndroidNative()) {
        const routes = getAvailableRoutes();
        if (routes.length > 0) setVoiceAvailableRoutes(routes);
      }
      const micPub = activeRoom.localParticipant.getTrackPublication(Track.Source.Microphone);
      const micTrack = micPub?.track?.mediaStreamTrack;
      if (micTrack) {
        const stream = new MediaStream([micTrack]);
        localStreamRef.current = stream;
        startMeter('self', stream);
      }
      setVoiceMuted(false);
      setVoiceJoined(true);
      setVoiceActiveChannelId(channelId);
      setVoiceActiveChannelName(channels.find(c => c.id === channelId)?.name || channelId);
      setVoiceActiveSpaceId(spaceId);
      try {
        socket.emit('voice:join', { spaceId, channelId: fq(spaceId, channelId) });
      } catch { /* noop */ }
      playVoiceChime("join");
      logVoice("join livekit", fq(spaceId, channelId));
      refreshLiveKitPeers(activeRoom);
    } catch (err: any) {
      console.error('LiveKit join failed', err);
      try { room?.disconnect(); } catch { /* noop */ }
      if (liveKitRoomRef.current === room) liveKitRoomRef.current = null;
      if (pendingJoinRef.current === room) pendingJoinRef.current = null;
      clearLiveKitState();
      setVoiceJoined(false);
      setVoiceActiveChannelId(null);
      setVoiceActiveChannelName(null);
      setVoiceActiveSpaceId(null);
      setVoiceMuted(false);
      voiceDeafenedRef.current = false;
      setVoiceDeafened(false);
      setVideoEnabled(false);
      setLocalVideoStream(null);
      toast(voiceJoinErrorMessage(err), 'error');
    }
  }
  function leaveVoice() {
    // Abort a join that never finished negotiating, otherwise its Room object leaks
    // and blocks the next join attempt.
    if (pendingJoinRef.current) {
      try { pendingJoinRef.current.disconnect(); } catch { /* noop */ }
      pendingJoinRef.current = null;
    }
    if (liveKitRoomRef.current) {
      try { liveKitRoomRef.current.disconnect(); } catch { /* noop */ }
      liveKitRoomRef.current = null;
    }
    try {
      const sid = voiceActiveSpaceId || currentVoidId;
      const cid = voiceActiveChannelId || currentChannelId;
      if (sid && cid) {
        socket.emit('voice:leave', { spaceId: sid, channelId: fq(sid, cid) });
      }
    } catch { /* noop */ }
    clearLiveKitState();
    if (localStreamRef.current) { for (const t of localStreamRef.current.getTracks()) { try { t.stop(); } catch { /* noop */ } } }
    localStreamRef.current = null;
    setLocalVideoStream(null);
    setVideoEnabled(false);
    stopMeter('self');
    // close audio context if any
    try { audioCtxRef.current?.close(); } catch { /* noop */ }
    audioCtxRef.current = null;
    setVoiceJoined(false);
    setVoiceActiveChannelId(null);
    setVoiceActiveChannelName(null);
    setVoiceActiveSpaceId(null);
    setVoiceMuted(false);
    voiceDeafenedRef.current = false;
    setVoiceDeafened(false);
    playVoiceChime("leave");
    logVoice("leave livekit");
  }

  async function confirmVoiceSwitch() {
    if (!voiceSwitchPrompt) return;
    const { channelId, spaceId } = voiceSwitchPrompt;
    setVoiceSwitchPrompt(null);
    leaveVoice();
    // Navigate to the target space/channel (parent-side callback)
    navigateToVoiceChannel(spaceId, channelId);
    // Small delay to let the disconnect settle before opening new room
    await new Promise<void>((r) => setTimeout(r, 300));
    // Pass target IDs directly so we don't rely on stale state
    await joinVoice(spaceId, channelId);
  }

  function toggleMute() {
    if (!liveKitRoomRef.current) {
      toast('Join the call before muting', 'info');
      return;
    }
    setVoiceMuted((m) => {
      const nm = !m;
      const room = liveKitRoomRef.current;
      if (room) {
        if (nm) {
          try { room.localParticipant.setMicrophoneEnabled(false); } catch {}
        } else {
          const captureOpts: any = {
            echoCancellation: true,
            noiseSuppression: true,
            channelCount: 1,
            autoGainControl: !isAndroidClient,
          };
          if (selectedInputId) captureOpts.deviceId = selectedInputId;
          void enableMicWithRetry(room, captureOpts).catch((e: any) => {
            toast(e?.message || 'Failed to unmute microphone', 'error');
          });
        }
      }
      return nm;
    });
  }
  async function startVideo() {
    if (!voiceJoined || videoEnabled || videoBusy) return;
    if (!liveKitRoomRef.current) {
      toast('Join the call before enabling video', 'info');
      return;
    }
    try {
      setVideoBusy(true);
      await liveKitRoomRef.current.localParticipant.setCameraEnabled(true);
      const pub = liveKitRoomRef.current.localParticipant.getTrackPublication("camera" as any);
      const track = pub?.track?.mediaStreamTrack;
      if (track) {
        setLocalVideoStream(new MediaStream([track]));
        setVideoEnabled(true);
      }
    } catch (e: any) {
      toast(e?.message || 'Camera unavailable on this device', 'error');
    } finally {
      setVideoBusy(false);
    }
  }
  async function stopVideo() {
    if (!liveKitRoomRef.current) {
      setLocalVideoStream(null);
      setVideoEnabled(false);
      return;
    }
    try { await liveKitRoomRef.current.localParticipant.setCameraEnabled(false); } catch { /* noop */ }
    setLocalVideoStream(null);
    setVideoEnabled(false);
  }
  async function toggleVideo() {
    if (!voiceJoined) {
      toast('Join the call before toggling video', 'info');
      return;
    }
    if (videoEnabled) await stopVideo();
    else await startVideo();
  }

  return {
    // session
    voiceJoined,
    voiceMuted,
    voiceDeafened,
    voiceActiveChannelId,
    voiceActiveChannelName,
    voiceActiveSpaceId,
    voiceSwitchPrompt,
    setVoiceSwitchPrompt,
    // presence & participants
    voicePresenceByChannel,
    getVoiceNamesForChannel,
    viewingActiveCall,
    voiceRoomParticipants,
    voiceVolumes,
    voiceSilenced,
    // video
    videoEnabled,
    videoBusy,
    // devices & audio
    devicesOpen,
    toggleDevices,
    inputs,
    outputs,
    selectedInputId,
    setSelectedInputId,
    selectedOutputId,
    setSelectedOutputId,
    needsAudioUnlock,
    setNeedsAudioUnlock,
    voiceAudioRoute,
    setVoiceAudioRoute,
    voiceAvailableRoutes,
    // refs consumed by VoiceChannelView and ChatLayout's switchChannel guard
    remoteStreamsRef,
    audioElsRef,
    liveKitRoomRef,
    // actions
    joinVoice,
    leaveVoice,
    confirmVoiceSwitch,
    toggleMute,
    toggleDeafen,
    toggleVideo,
    switchMicrophone,
    applyOutputDevice,
    refreshDevices,
    unlockRemoteAudio,
    setParticipantVolume,
  };
}


